import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Autocomplete } from '@material-ui/lab';
import { spacing } from '@material-ui/system';
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  InputLabel,
  Divider as MuiDivider,
  MenuItem,
  Select,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  TextField,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';

import {
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Label as LabelIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';

import {  KeyboardDatePicker } from '@material-ui/pickers';
import { useUserState } from '../../context/UserContext';
import { AssessmentFilterProps } from '../../common/entityUtilities';
import { useTranslation } from 'react-i18next';

import { useUserListState } from '../../context/UserListContext';
import { getSorting, stableSort } from '../../common/tableUtilities';
import { debugLog } from '../../common/debugUtilities';


const Divider = styled(MuiDivider)(spacing);
const Spacer = styled.div`
  flex: 1 1 100%;
`;

// Basically a duplicate of the VideoTableToolbar...
const AssessmentTableFilter = props => {

  const user = useUserState();
  const { allUsers } = useUserListState();
  const {handleFilter, handleResetFilter} = props;
  const { t, i18n } = useTranslation();

  // Get date format based on locale
  const dateFormat = i18n.language === 'es' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';

  var sevendays = new Date();
  var nowdate = new Date();
  // findWithNoItems/findWithNoReport are unused today but retained so a future
  // "filter by report status" feature can add them without churning state shape.
  // disableDates is now derived from dateRangeOption rather than user-toggled.
  const [checkedState, setCheckedState] = useState({findWithNoItems:true, findWithNoReport:false, disableDates:true});

  const [searchStartDate, setSearchStartDate] = useState(sevendays.setDate(sevendays.getDate()-7));
  const [searchEndDate, setSearchEndDate] = useState(nowdate.setDate(nowdate.getDate()));
  // Date range selector. Default to 'all' — when a user picks a parent email
  // without thinking about dates, they almost always want any video for that
  // parent. The previous default ('last 7 days', hidden behind a checkbox)
  // produced confusing empty results.
  const [dateRangeOption, setDateRangeOption] = useState('all');
  const [searchDisabled, setSearchDisabled] = useState(false);
  
  const [currentSelectedParent, setCurrentSelectedParent] = useState(null);
  const [parentEmailForSearch, setParentEmailForSearch] = useState('');
  const [subjectIdForSearch, setSubjectIdForSearch] = useState('');
  const [filterExpanded, setFilterExpanded] = useState(false);
  // UX: show a spinner inside the Apply button while the filter API call is in
  // flight, and surface a toaster when the result set comes back empty.
  const [isApplying, setIsApplying] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');


  useEffect(()=>{
    // When dates aren't part of the query (Last 7 / Last month / All),
    // date validity shouldn't gate the Apply button.
    if (checkedState.disableDates){
      setSearchDisabled(false);
      return;
    }
    if(searchEndDate === null || searchStartDate === null ||
      isNaN(searchStartDate) || isNaN(searchEndDate)){
        debugLog('Disabling Filter');
      setSearchDisabled(true);
      debugLog('Search Start Date: ', searchStartDate);
      debugLog('Search End Date: ', searchEndDate);
      return;
    }
    setSearchDisabled(false);
  },[searchStartDate,searchEndDate, checkedState.disableDates]);

  // Map dropdown choice -> derived start/end dates and the disableDates flag.
  // 'custom' leaves whatever the user has in the pickers; everything else
  // computes the range here and skips the date filter for 'all'.
  useEffect(() => {
    const now = new Date();
    if (dateRangeOption === 'week') {
      const start = new Date(); start.setDate(start.getDate() - 7);
      setSearchStartDate(start);
      setSearchEndDate(now);
      setCheckedState((prev) => ({ ...prev, disableDates: false }));
    } else if (dateRangeOption === 'month') {
      const start = new Date(); start.setDate(start.getDate() - 30);
      setSearchStartDate(start);
      setSearchEndDate(now);
      setCheckedState((prev) => ({ ...prev, disableDates: false }));
    } else if (dateRangeOption === 'all') {
      setCheckedState((prev) => ({ ...prev, disableDates: true }));
    } else if (dateRangeOption === 'custom') {
      setCheckedState((prev) => ({ ...prev, disableDates: false }));
    }
  }, [dateRangeOption]);

  // Copied from AsmtAndUserControlComponent
  useEffect(()=>{
    debugLog('The email;,', parentEmailForSearch);
    if(parentEmailForSearch === null){
      setCurrentSelectedParent(null);
      setSubjectIdForSearch(undefined);
      return;
    }

    const matchingParent = allUsers.find(user=>user.localEmail === parentEmailForSearch);
    debugLog('The matching email ', matchingParent);
    if(matchingParent){
      
      setCurrentSelectedParent(matchingParent);
      if(matchingParent.subject){
        setSubjectIdForSearch(matchingParent.subject?.subjectId);
        debugLog('subject id:', matchingParent.subject?.subjectId);
      }else{
        // parent has no subject adjust accordingly
        setSubjectIdForSearch('shouldnotmatchanything');
      }      
    }
  }, [parentEmailForSearch]);

  useEffect(()=>{
    if(subjectIdForSearch === null){
      setCurrentSelectedParent(null);
      setSubjectIdForSearch(undefined);
      return;
    }
    const matchingParent = allUsers.find(user=>user.subject?.subjectId === subjectIdForSearch);
    debugLog('The parent for subject ', matchingParent);
    if(matchingParent){
      
      setCurrentSelectedParent(matchingParent);
      if(matchingParent.subject){
        setParentEmailForSearch(matchingParent.localEmail);
      }else{
        // parent has no subject adjust accordingly
        setParentEmailForSearch('noemail@found.here');
      }      
    }
  }, [subjectIdForSearch]);

  // Restore the default (unfiltered) list and clear the filter controls.
  // Shares the isApplying spinner state with Apply.
  const resetFilter = async () => {
    setIsApplying(true);
    try {
      // Clear controls back to their initial values.
      const seven = new Date();
      const now = new Date();
      setSearchStartDate(seven.setDate(seven.getDate() - 7));
      setSearchEndDate(now.setDate(now.getDate()));
      setCheckedState({ findWithNoItems: true, findWithNoReport: false, disableDates: true });
      setDateRangeOption('all');
      setCurrentSelectedParent(null);
      setParentEmailForSearch('');
      setSubjectIdForSearch('');
      if (handleResetFilter) {
        await handleResetFilter();
      }
    } catch (err) {
      debugLog('Filter reset failed:', err);
    } finally {
      setIsApplying(false);
    }
  };

  // This is the show button, which only sets a new filter, the table calls the actual action.
  // handleFilter is async upstream; await it so we can spin the button and report empty results.
  const updateFilteredAsmtList = async () => {
    const asmtSearchProps = {startDate:searchStartDate, endDate:searchEndDate,
        includeAllDates:checkedState, parentEmail: parentEmailForSearch};
    setIsApplying(true);
    try {
      const result = await handleFilter(asmtSearchProps);
      const count = result?.count;
      if (count === 0) {
        setSnackbarMessage(t('nomatches', {ns: 'asmttablehead'}));
        setSnackbarOpen(true);
      }
    } catch (err) {
      debugLog('Filter apply failed:', err);
      setSnackbarMessage(t('filtererror', {ns: 'asmttablehead'}));
      setSnackbarOpen(true);
    } finally {
      setIsApplying(false);
    }
  };

  //these make the search start/end date inclusive
  const setSearchStartDateStart = (date) => {
    if(!isNaN(date) && date !== null) {
      date.setHours(0, 0, 0, 0);
    }    
    setSearchStartDate(date);
  };

  const setSearchEndDateEnd = (date) => {
    if(!isNaN(date) && date !== null) {
      date.setHours(23, 59, 59, 999);
    }    
    setSearchEndDate(date);
  };
  const handleChange = (event) => {
    setCheckedState({ ...checkedState, [event.target.name]: event.target.checked });
    debugLog('include all dates:',event.target.checked);
  };

  return (
    <div>
      <Accordion expanded={filterExpanded} onChange={() => setFilterExpanded(!filterExpanded)} style={{ marginBottom: 16 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="filter-content"
          id="filter-header"
        >
          <FilterListIcon style={{ marginRight: 8 }} />
          <Typography>{t('filtervideos', {ns: 'asmttablehead'})}</Typography>
        </AccordionSummary>
        <AccordionDetails style={{ display: 'block', padding: '16px' }}>
          <Grid
            container
            direction="column"
            spacing={2}
            style={{ width: '100%' }}
          >
          <Grid
            align="left"
            container
            item
            justify="left"
            md={12}
            spacing={2}
            xs={12}
            sm={12}
          >
            {/* Layout:
                  - non-custom: Date Range (4/12) + Parent Email (8/12) on one row.
                  - custom:     Date Range (4/12) + From (4/12) + To (4/12) on row 1,
                                Parent Email (12/12) on row 2. */}
            {/* sm widths shift based on mode so all three controls fit on one row
                in Custom mode at tablet widths (e.g. iPad mini at 768px). */}
            <Grid item xs={12} sm={dateRangeOption === 'custom' ? 4 : 6} md={4}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="date-range-label">
                  {t('daterangelabel', {ns: 'asmttablehead'})}
                </InputLabel>
                <Select
                  labelId="date-range-label"
                  id="date-range-select"
                  value={dateRangeOption}
                  onChange={(event) => setDateRangeOption(event.target.value)}
                >
                  <MenuItem value="all">{t('daterange_all', {ns: 'asmttablehead'})}</MenuItem>
                  <MenuItem value="week">{t('daterange_week', {ns: 'asmttablehead'})}</MenuItem>
                  <MenuItem value="month">{t('daterange_month', {ns: 'asmttablehead'})}</MenuItem>
                  <MenuItem value="custom">{t('daterange_custom', {ns: 'asmttablehead'})}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {dateRangeOption === 'custom' && (
              <>
                <Grid item xs={12} sm={4} md={4}>
                  <KeyboardDatePicker
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    format={dateFormat}
                    label={t('from', {ns: 'asmttablehead'})}
                    margin="dense"
                    onChange={setSearchStartDateStart}
                    value={searchStartDate}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <KeyboardDatePicker
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    format={dateFormat}
                    label={t('to', {ns: 'asmttablehead'})}
                    margin="dense"
                    onChange={setSearchEndDateEnd}
                    value={searchEndDate}
                    fullWidth
                  />
                </Grid>
              </>
            )}
            {user.authLevel > 0 && (

              <Grid
                item
                xs={12}
                sm={12}
                md={dateRangeOption === 'custom' ? 12 : 8}
              >
                <Autocomplete
                  autoHighlight
                  blurOnSelect
                  getOptionLabel={(option)=> (option && option.localEmail)?option.localEmail:'unknown'}
                  handleHomeEndKeys
                  id="parent-email-selector"
                  onChange={(event, newValue) => {
                    debugLog('Selected parent:',newValue);
                    if(newValue && newValue.localEmail){
                      setParentEmailForSearch(newValue.localEmail);
                    }else{
                      setParentEmailForSearch(null);
                    }
                  }}
                  options={stableSort(allUsers,getSorting('asc','localEmail'))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('parentemail', {ns: 'asmttablehead'})}
                      variant="outlined"
                      margin="dense"
                    />
                  )}
                  selectOnFocus
                  value={currentSelectedParent}
                />
              </Grid>)}

          </Grid>

          <Grid
            item
            xs={12}
            style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}
          >
            <Button
              color="primary"
              disabled={isApplying}
              onClick={resetFilter}
              size="medium"
              variant="outlined"
            >
              {t('resetfilters', {ns: 'asmttablehead'})}
            </Button>
            <Button
              color="primary"
              disabled={searchDisabled || isApplying}
              onClick={updateFilteredAsmtList}
              size="medium"
              variant="contained"
              startIcon={isApplying ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {t('applyfilters', {ns: 'asmttablehead'})}
            </Button>
          </Grid>

        </Grid>
        </AccordionDetails>
      </Accordion>
      <Snackbar
        autoHideDuration={4000}
        message={snackbarMessage}
        onClose={() => setSnackbarOpen(false)}
        open={snackbarOpen}
      />
    </div>
  );
};

export default AssessmentTableFilter;
